/**
 * decisionAnswerOutcome — what actually happened when somebody answered a decision.
 *
 * emuw-v1-5 `shared-decision-components` Phase 1 task 1.
 *
 * Both hosts classify into THIS type. The shapes they classify FROM differ — Ora's
 * Remix action returns JSON, the add-in throws an ApiError — but the meaning must
 * not, and today it does: the add-in's `resumeInstance` is typed `Promise<void>`
 * and Ora's action answers `{ok:true}` for any 2xx, so both surfaces throw away
 * the substrate's refusal and tell the AM their answer landed.
 *
 * "REFUSED" IS NOT AN ERROR. The substrate did exactly the right thing: somebody
 * else already answered this decision. It is a state the surface has to present in
 * plain language and then stop submitting against — not a failure to retry, and
 * not a success to celebrate.
 *
 * The two refusal signals are deliberately narrow:
 *
 *   HTTP 409     — a COLD gate that was already answered.
 *   200 + status "already_handled" — a WARM gate that was. A 200 that is really a
 *                  refusal is the worse of the two failure modes, because it tells
 *                  the AM their second answer landed.
 *
 * Widening either would be worse than the bug being fixed. Treating, say, a 400 as
 * already-answered would latch the controls closed on a recoverable validation
 * failure and strand the AM with no way to answer at all.
 *
 * PURE MODULE: no React, no fetch, no host import, no `*.server` import. Both Jest
 * projects and the add-in's Vite build import it directly (design Constraint 20).
 */

export type DecisionAnswerOutcome =
  | { kind: "accepted" }
  | { kind: "refused"; reason: "already-answered"; detail: string | null }
  | { kind: "error"; message: string };

/**
 * The substrate ack status meaning "the gate was already resolved".
 *
 * Emitted by `resume_cancel.py`'s warm-hold branch with HTTP 200, which is why it
 * cannot be detected from the status code alone.
 */
export const ALREADY_HANDLED_STATUS = "already_handled";

/** HTTP Conflict — the substrate's cold-gate "already answered". */
export const ALREADY_ANSWERED_HTTP_STATUS = 409;

function firstString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Classify a 2xx acknowledgement body.
 *
 * Anything that is not literally `already_handled` is `accepted` — including an
 * absent status, an empty body and an unrecognised string. A 2xx from the resume
 * endpoint means the substrate took the answer; only the one sentinel says
 * otherwise, and inventing further refusal statuses here would silently latch
 * controls closed on acks this code does not yet know about.
 */
export function classifyResumeAck(ack: unknown): DecisionAnswerOutcome {
  const body = (ack ?? {}) as Record<string, unknown>;
  const status = typeof body.status === "string" ? body.status : "";
  if (status === ALREADY_HANDLED_STATUS) {
    return {
      kind: "refused",
      reason: "already-answered",
      // `detail` is whatever the substrate said about it, if anything. Never
      // fabricated — the copy module owns the fallback wording.
      detail: firstString(body.detail, body.message, body.reason),
    };
  }
  return { kind: "accepted" };
}

/**
 * Classify a thrown failure carrying an HTTP status.
 *
 * `refused` ONLY for 409. Everything else — including `null`, which is what an
 * offline fetch or an unparseable failure produces — is `error`, because the AM's
 * answer did not land and they must be able to try again.
 */
export function classifyResumeFailure(
  status: number | null,
  message: string,
): DecisionAnswerOutcome {
  if (status === ALREADY_ANSWERED_HTTP_STATUS) {
    return {
      kind: "refused",
      reason: "already-answered",
      detail: message?.trim() ? message.trim() : null,
    };
  }
  return { kind: "error", message: message?.trim() || "Could not submit your answer." };
}

/** Convenience for call sites that only need "should I stop submitting?". */
export function isRefusal(
  outcome: DecisionAnswerOutcome,
): outcome is Extract<DecisionAnswerOutcome, { kind: "refused" }> {
  return outcome.kind === "refused";
}
